import { studyQueryKeys } from '@/config/queryKeys';
import { STUDIES_QUERY_STALE_TIME, STUDIES_QUERY_GC_TIME } from '@/constants';
import { searchStudies } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import type { InstrumentRunDataRow, PaginatedStudiesResponse } from '@/types';
import type { SelectionOption } from '@/types/autocomplete';
import { mapStudiesToOptions } from '@/utils/autocomplete';
import { useQuery } from '@tanstack/react-query';
import type { ICellRendererParams } from 'ag-grid-enterprise';
import type { FC } from 'react';
import React, { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { SelectionCellRenderer } from './shared/SelectionCellRenderer';

interface StudyNameCellRendererProps extends ICellRendererParams<InstrumentRunDataRow> {
  showCopyToRows?: boolean;
  updateSampleAt?: (rowIndex: number, patch: Partial<InstrumentRunDataRow>) => void;
  allRowData?: InstrumentRunDataRow[];
  onBulkApply?: (rows: InstrumentRunDataRow[]) => void;
}

/**
 * Custom cell renderer for Study Name selection with Dialog interface
 * Uses the shared SelectionCellRenderer for consistent UX
 * Supports bulk updating empty study names via checkbox, or copy-to-rows in edit mode
 */
export const StudyNameCellRenderer: FC<StudyNameCellRendererProps> = ({
  showCopyToRows = false,
  updateSampleAt,
  allRowData,
  onBulkApply,
  ...props
}) => {
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization));

  const { data: studiesData } = useQuery<PaginatedStudiesResponse>({
    queryKey: selectedOrg?.id ? studyQueryKeys.listSearch(selectedOrg.id) : ['disabled-studies-query'],
    queryFn: async () => {
      if (!selectedOrg?.id) return { records: [], total: 0, skip: 0, limit: 0, has_more: false };
      return searchStudies(selectedOrg.id, {
        skip: 0,
        limit: 500,
        sort_by: 'created_at',
        sort_order: 'desc',
      });
    },
    enabled: !!selectedOrg?.id,
    staleTime: STUDIES_QUERY_STALE_TIME,
    gcTime: STUDIES_QUERY_GC_TIME,
    refetchOnMount: false,
  });

  const studyOptions = useMemo<SelectionOption[]>(() => {
    if (!studiesData?.records) return [];
    return mapStudiesToOptions(studiesData.records);
  }, [studiesData]);

  // When a study is selected, also populate org_name from the study's org_name field.
  // StudyPublicGetMany includes org_name on every record — no secondary org lookup needed.
  const getExtraFields = useCallback(
    (selectedOption: SelectionOption): Partial<InstrumentRunDataRow> => {
      const study = studiesData?.records.find(s => String(s.id) === String(selectedOption.key));
      return study?.org_name ? { org_name: study.org_name, org_id: study.org_id ?? undefined } : {};
    },
    [studiesData],
  );

  return (
    <SelectionCellRenderer
      {...props}
      allRowData={allRowData}
      applyToEmptyKey="study_apply_to_all_empty"
      dialogTitleKey="study_select_dialog_title"
      emptyHintKey="study_select_empty_hint"
      fieldName="study_name"
      getExtraFields={getExtraFields}
      onBulkApply={onBulkApply}
      options={studyOptions}
      placeholderKey="study_select_placeholder"
      showCopyToRows={showCopyToRows}
      updateSampleAt={updateSampleAt}
    />
  );
};

StudyNameCellRenderer.displayName = 'StudyNameCellRenderer';

export default StudyNameCellRenderer;
